import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { DineInQrCode } from 'api/controllers/tableMaps';
import { EditorTable, TableShape } from '../context';

const MAGENTA = theme.palette.primary.main;
const ERROR_COLOR = theme.palette.error.main;

const SEATS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '20px 20px 24px',
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        height: '100%',
        overflowY: 'auto' as const,
        boxSizing: 'border-box' as const,
    },
    title: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.palette.primary.dark,
        letterSpacing: 0.4,
        textTransform: 'uppercase' as const,
        marginBottom: 4,
    },
    fieldWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    sizeRow: {
        display: 'flex',
        gap: 12,
    },
    errorText: {
        fontSize: 11,
        color: ERROR_COLOR,
        marginTop: 2,
    },
    activeRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    activeLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    deleteButton: {
        color: ERROR_COLOR,
        justifyContent: 'flex-start',
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: 13,
        padding: '4px 0',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
    },
    deleteIcon: {
        fontSize: 18,
        marginRight: 6,
        color: ERROR_COLOR,
    },
    saveButton: {
        backgroundColor: MAGENTA,
        color: theme.palette.common.white,
        fontWeight: 600,
        letterSpacing: 1,
        marginTop: 8,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.main,
        },
    },
    divider: {
        borderTop: `1px solid ${theme.palette.grey['300']}`,
        margin: '4px 0',
    },
    inputLabel: {
        maxWidth: 'calc(100% - 32px)',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&.MuiInputLabel-shrink': {
            maxWidth: 'calc(133% - 32px)',
        },
    },
});

interface TableSettingsPanelProps {
    table: EditorTable;
    qrCodes: DineInQrCode[];
    otherUsedQrCodes: string[];
    otherTableNames: string[];
    originalActiveQrCodes: string[];
    onSave: (patch: Partial<Omit<EditorTable, 'id'>>) => void;
    onDelete: () => void;
    onRotate: (rotation: number) => void;
}

export default function TableSettingsPanel({
    table,
    qrCodes,
    otherUsedQrCodes,
    otherTableNames,
    originalActiveQrCodes,
    onSave,
    onDelete,
    onRotate,
}: TableSettingsPanelProps) {
    const classes = useStyles();

    const [name, setName] = useState(table.label);
    const [seats, setSeats] = useState(table.seats);
    const [shape, setShape] = useState<TableShape>(table.shape);
    const [qrCode, setQrCode] = useState<string | null>(table.qrCode || null);
    const [isActive, setIsActive] = useState(table.isActive);
    const [rotationInput, setRotationInput] = useState(String(table.rotation));
    const lastCommittedRotation = useRef(table.rotation);
    // Width/height have no live canvas actor (unlike rotation's drag handle), so they
    // commit together with the rest of the form on Save rather than dispatching live.
    const [width, setWidth] = useState(table.width);
    const [widthInput, setWidthInput] = useState(String(table.width));
    const [height, setHeight] = useState(table.height);
    const [heightInput, setHeightInput] = useState(String(table.height));
    const [nameError, setNameError] = useState(false);
    const [qrTouched, setQrTouched] = useState(false);

    useEffect(() => {
        setName(table.label);
        setSeats(table.seats);
        setShape(table.shape);
        setQrCode(table.qrCode || null);
        setIsActive(table.isActive);
        setRotationInput(String(table.rotation));
        lastCommittedRotation.current = table.rotation;
        setWidth(table.width);
        setWidthInput(String(table.width));
        setHeight(table.height);
        setHeightInput(String(table.height));
        setNameError(false);
        setQrTouched(false);
    }, [table.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (table.rotation !== lastCommittedRotation.current) {
            setRotationInput(String(table.rotation));
            lastCommittedRotation.current = table.rotation;
        }
    }, [table.rotation]);

    // isUsed means "used by ANOTHER map's active table". A QR that belonged to
    // this map's original active tables is not used elsewhere — the draft's own
    // otherUsedQrCodes (active-only) is the authoritative in-map source.
    const availableQrCodes = qrCodes.filter(
        (qr) =>
            qr.name === table.qrCode ||
            qr.name === qrCode ||
            ((!qr.isUsed || originalActiveQrCodes.includes(qr.name)) &&
                !otherUsedQrCodes.includes(qr.name))
    );

    const qrError = isActive && !!qrCode && otherUsedQrCodes.includes(qrCode);
    const qrRequired = isActive && !qrCode;
    const nameDuplicate = name.trim() !== '' && otherTableNames.includes(name.trim().toLowerCase());
    const parsedRotation = Number(rotationInput);
    const rotationInvalid =
        rotationInput.trim() === '' ||
        Number.isNaN(parsedRotation) ||
        parsedRotation < 0 ||
        parsedRotation > 359;

    const canSave =
        name.trim() !== '' && !qrError && !qrRequired && !nameDuplicate && !rotationInvalid;

    function handleSave() {
        if (name.trim() === '') {
            setNameError(true);
            return;
        }
        if (qrRequired) {
            setQrTouched(true);
            return;
        }
        if (qrError || nameDuplicate) return;
        onSave({
            label: name.trim(),
            seats,
            shape,
            qrCode,
            isActive,
            width,
            height,
        });
    }

    function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
        const val = e.target.value;
        setName(val);
        if (nameError && val.trim() !== '') {
            setNameError(false);
        }
    }

    // Valid table footprint: 0 (unset — falls back to the shape default) or 0.2..10 m.
    function isValidTableSize(n: number): boolean {
        return n === 0 || (n >= 0.2 && n <= 10);
    }

    function handleWidthChange(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value;
        setWidthInput(raw);
        const n = Number(raw);
        if (raw.trim() === '' || Number.isNaN(n) || !isValidTableSize(n)) return;
        setWidth(Math.round(n * 100) / 100);
    }

    function handleHeightChange(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value;
        setHeightInput(raw);
        const n = Number(raw);
        if (raw.trim() === '' || Number.isNaN(n) || !isValidTableSize(n)) return;
        setHeight(Math.round(n * 100) / 100);
    }

    function handleQrChange(e: React.ChangeEvent<{ value: unknown }>) {
        const val = e.target.value as string;
        setQrCode(val === '' ? null : val);
        setQrTouched(true);
    }

    function handleShapeChange(e: React.ChangeEvent<{ value: unknown }>) {
        setShape(e.target.value as TableShape);
    }

    function handleSeatsChange(e: React.ChangeEvent<{ value: unknown }>) {
        setSeats(Number(e.target.value));
    }

    function handleActiveChange(e: React.ChangeEvent<HTMLInputElement>) {
        setIsActive(e.target.checked);
    }

    function handleRotationChange(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value;
        setRotationInput(raw);

        const n = Number(raw);
        if (raw.trim() === '' || Number.isNaN(n) || n < 0 || n > 359) return;
        const rounded = Math.round(n);
        lastCommittedRotation.current = rounded;
        onRotate(rounded);
    }

    return (
        <div className={classes.root}>
            <Typography className={classes.title}>
                {i18n.t('tableMaps.wizard.tableSettings')}
            </Typography>

            <div className={classes.fieldWrap}>
                <TextField
                    label={i18n.t('tableMaps.wizard.tableNumberName')}
                    value={name}
                    onChange={handleNameChange}
                    variant="filled"
                    fullWidth
                    error={nameError || nameDuplicate}
                    inputProps={{ 'aria-label': i18n.t('tableMaps.wizard.tableNumberName') }}
                />
                {nameError && (
                    <Typography className={classes.errorText}>
                        {i18n.t('tableMaps.wizard.nameRequired')}
                    </Typography>
                )}
                {!nameError && nameDuplicate && (
                    <Typography className={classes.errorText}>
                        {i18n.t('tableMaps.wizard.nameDuplicate')}
                    </Typography>
                )}
            </div>

            <FormControl variant="filled" fullWidth>
                <InputLabel id="seats-label" className={classes.inputLabel}>
                    {i18n.t('tableMaps.wizard.seats')}
                </InputLabel>
                <Select labelId="seats-label" value={seats} onChange={handleSeatsChange}>
                    {SEATS_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                            {n}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl variant="filled" fullWidth>
                <InputLabel id="shape-label" className={classes.inputLabel}>
                    {i18n.t('tableMaps.wizard.shape')}
                </InputLabel>
                <Select labelId="shape-label" value={shape} onChange={handleShapeChange}>
                    <MenuItem value="round">{i18n.t('tableMaps.wizard.roundTable')}</MenuItem>
                    <MenuItem value="square">{i18n.t('tableMaps.wizard.squareTable')}</MenuItem>
                    <MenuItem value="rectangle">
                        {i18n.t('tableMaps.wizard.rectangleTable')}
                    </MenuItem>
                </Select>
            </FormControl>

            <div className={classes.sizeRow}>
                <TextField
                    label={i18n.t('tableMaps.wizard.tableWidth')}
                    type="number"
                    value={widthInput}
                    onChange={handleWidthChange}
                    variant="filled"
                    fullWidth
                    inputProps={{
                        min: 0,
                        max: 10,
                        step: 0.01,
                        'aria-label': i18n.t('tableMaps.wizard.tableWidth'),
                    }}
                />
                <TextField
                    label={i18n.t('tableMaps.wizard.tableLength')}
                    type="number"
                    value={heightInput}
                    onChange={handleHeightChange}
                    variant="filled"
                    fullWidth
                    inputProps={{
                        min: 0,
                        max: 10,
                        step: 0.01,
                        'aria-label': i18n.t('tableMaps.wizard.tableLength'),
                    }}
                />
            </div>

            <TextField
                label={i18n.t('tableMaps.wizard.rotation')}
                type="number"
                value={rotationInput}
                onChange={handleRotationChange}
                variant="filled"
                fullWidth
                inputProps={{
                    min: 0,
                    max: 359,
                    step: 1,
                    'aria-label': i18n.t('tableMaps.wizard.rotation'),
                }}
            />

            <div className={classes.fieldWrap}>
                <FormControl
                    variant="filled"
                    fullWidth
                    error={qrError || (qrRequired && qrTouched)}
                >
                    <InputLabel id="qr-label" className={classes.inputLabel}>
                        {i18n.t('tableMaps.wizard.linkedQrCode')}
                    </InputLabel>
                    <Select labelId="qr-label" value={qrCode ?? ''} onChange={handleQrChange}>
                        <MenuItem value="">{i18n.t('tableMaps.wizard.noQrLinked')}</MenuItem>
                        {availableQrCodes.map((qr) => (
                            <MenuItem key={qr.name} value={qr.name}>
                                {qr.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {qrError && (
                    <Typography className={classes.errorText}>
                        {i18n.t('tableMaps.wizard.qrInUse')}
                    </Typography>
                )}
                {!qrError && qrRequired && qrTouched && (
                    <Typography className={classes.errorText}>
                        {i18n.t('tableMaps.wizard.qrRequired')}
                    </Typography>
                )}
            </div>

            <div className={classes.activeRow}>
                <Typography className={classes.activeLabel}>
                    {i18n.t('tableMaps.wizard.tableActive')}
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isActive}
                            onChange={handleActiveChange}
                            color="primary"
                            name="isActive"
                        />
                    }
                    label={
                        isActive
                            ? i18n.t('tableMaps.wizard.active')
                            : i18n.t('tableMaps.wizard.inactive')
                    }
                />
            </div>

            <div className={classes.divider} />

            <Button
                className={classes.deleteButton}
                disableRipple
                onClick={onDelete}
                startIcon={<DeleteOutlineIcon className={classes.deleteIcon} />}
            >
                {i18n.t('tableMaps.wizard.delete')}
            </Button>

            <Button
                variant="contained"
                fullWidth
                className={classes.saveButton}
                onClick={handleSave}
                disabled={!canSave}
            >
                {i18n.t('tableMaps.wizard.saveChanges')}
            </Button>
        </div>
    );
}
